import React, { useRef, useState, useCallback, useEffect } from "react";

interface ProductCarouselProps {
  products: React.ReactNode[];
}

const ProductCarousel: React.FC<ProductCarouselProps> = ({ products }) => {
  const carouselRef = useRef<HTMLDivElement>(null);

  // The track starts at its left edge, so the back arrow has nothing to point
  // at until the reader has scrolled. Hiding it there buys back the gutter it
  // was occupying, which is most of the point at phone widths.
  const [atStart, setAtStart] = useState(true);

  // A track whose cards all fit needs no arrows at all — and no reserved
  // gutter for them. On the homepage that lets a short Classics group shrink to
  // its content width and hand the leftover room to Designs.
  const [overflows, setOverflows] = useState(false);

  const syncEdges = useCallback(() => {
    const container = carouselRef.current;
    if (!container) return;
    // 1px fudge for sub-pixel scroll positions.
    setAtStart(container.scrollLeft <= 1);
    setOverflows(container.scrollWidth - container.clientWidth > 1);
  }, []);

  // Products arrive asynchronously, so the first paint can be an empty track;
  // the observer also catches the container being resized (window resize, or
  // the sibling carousel taking a different share of the row).
  useEffect(() => {
    syncEdges();
    const container = carouselRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(syncEdges);
    observer.observe(container);
    return () => observer.disconnect();
  }, [syncEdges, products.length]);

  const handleScroll = (direction: "left" | "right") => {
    const container = carouselRef.current;
    if (!container) return;

    const cardWidth = container.firstElementChild?.clientWidth || 0;
    const gap = 16; // matches space-x-4 (1rem = 16px)
    // Advance by one visible page, measured from the TRACK, not the viewport:
    // the homepage renders two of these side by side in half-width columns, and
    // a viewport-derived "4 cards per jump" over-scrolled past ~2 visible cards
    // there. Clamped to at least one card so a container narrower than a card
    // still moves.
    // pl-2 + the trailing gutter, which clientWidth includes. Asymmetric
    // because the forward arrow sits over the track's right edge — and there's
    // no gutter at all when the cards fit and the arrow is hidden.
    const trackPadding = 8 + (overflows ? 40 : 8);
    const visibleWidth = container.clientWidth - trackPadding;
    const scrollAmount = Math.max(
      cardWidth + gap,
      Math.floor(visibleWidth / (cardWidth + gap)) * (cardWidth + gap),
    );
    const maxScroll = container.scrollWidth - container.clientWidth;

    // Endless carousel: wrap to the other end instead of stopping. The 1px
    // fudge absorbs sub-pixel rounding so "at the end" is detected reliably.
    if (direction === "right") {
      if (container.scrollLeft >= maxScroll - 1) {
        container.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        container.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
    } else {
      if (container.scrollLeft <= 1) {
        container.scrollTo({ left: maxScroll, behavior: "smooth" });
      } else {
        container.scrollBy({ left: -scrollAmount, behavior: "smooth" });
      }
    }
  };

  const arrowButtonClasses =
    "absolute top-1/2 -translate-y-1/2 z-10 flex items-center justify-center " +
    "w-10 h-10 rounded-full bg-white/90 backdrop-blur text-gray-700 " +
    "shadow-md ring-1 ring-black/5 transition-all duration-200 " +
    "hover:bg-white hover:text-red-600 hover:scale-110 hover:shadow-lg " +
    "active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500";

  return (
    <div className="relative w-full">
      {/* Left Arrow — omitted at the left edge (see `atStart`). */}
      <button
        onClick={() => handleScroll("left")}
        aria-label="Previous products"
        className={`${arrowButtonClasses} left-1 sm:left-2 ${
          atStart || !overflows ? "hidden" : ""
        }`}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-5 h-5 -ml-0.5"
          aria-hidden="true"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>

      {/* Carousel Row — native horizontal scroll with snap.
          Letting the browser own the gesture means it locks to a single axis
          per swipe, so a horizontal swipe on mobile no longer drags the page
          up and down.

          One row of fixed-width cards at every width, phones included. Mobile
          used to chunk into full-width 2x2 pages, which was reasonable for a
          single homepage carousel but stacks to four rows of products once the
          homepage splits into Classics and Designs — pushing everything below
          it off the first screen. */}
      <div
        ref={carouselRef}
        onScroll={syncEdges}
        // The pr-10 gutter exists only to keep the forward arrow off the last
        // card. With no overflow there's no arrow, so dropping it back to pr-2
        // is what actually gives the room back. Both states are self-consistent
        // (a fitting track stays fitting at pr-2; an overflowing one stays
        // overflowing at pr-10), so this can't flip-flop.
        className={`flex overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-smooth space-x-4 pl-2 scroll-pl-2 ${
          overflows ? "pr-10" : "pr-2"
        }`}
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {products.map((product, i) => (
          <div
            key={i}
            className="shrink-0 snap-start w-32 sm:w-36 md:w-44 lg:w-48"
          >
            {product}
          </div>
        ))}
      </div>

      {/* Right Arrow — like the left one, gone when there's nothing to scroll to. */}
      <button
        onClick={() => handleScroll("right")}
        aria-label="Next products"
        className={`${arrowButtonClasses} right-1 sm:right-2 ${
          overflows ? "" : "hidden"
        }`}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-5 h-5 ml-0.5"
          aria-hidden="true"
        >
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    </div>
  );
};

export default ProductCarousel;
